import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import checkedRoundIcon from "../../../img/svg/checked_round_icon.svg";
import PartnerFooter from "../../components/PartnerFooter";
import ScreenHeader from "../../components/ScreenHeader";
import {
  checkCanPurchasePass,
  selectCanPurchaseChecked,
  selectCanPurchaseLoading,
  selectCanPurchaseNewPass,
} from "../../../store/multipleEntryPassSlice";
import { selectPartner } from "../../../store/partnerStore";
import { isV2Enabled, V2_SCREEN_KEYS } from "../../registry";
import "./howItWorks.scss";

// V2 "איך זה עובד" explainer (/how-it-works) — the monthly entitlement rules:
// the 4-entry pass, single entries, and full price once the entitlement is
// used up. Reached from the profile page's settings row and from the
// discover-pass-card page's "גלו איך זה עובד" link. The pass-card promo that
// used to live at this route is now /discover-pass-card.

// The asset draws both the ring and the check, so the badge supplies only the
// tinted background behind it.
const CheckIcon = () => (
  <span className="hiw-v2-section-icon">
    <img src={checkedRoundIcon} alt="" />
  </span>
);

function InfoSection({ title, body }) {
  return (
    <div className="hiw-v2-section">
      <CheckIcon />
      <div className="hiw-v2-section-texts">
        <p className="hiw-v2-section-title">{title}</p>
        <p className="hiw-v2-section-body">{body}</p>
      </div>
    </div>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const partner = useSelector(selectPartner);
  const canPurchaseNewPass = useSelector(selectCanPurchaseNewPass);
  const canPurchaseChecked = useSelector(selectCanPurchaseChecked);
  const canPurchaseLoading = useSelector(selectCanPurchaseLoading);
  const mainUserParams = useSelector((state) => state.userSlice.mainUserData);

  // Self-heal for a direct URL hit, where home never ran the purchase-eligibility
  // check — same pattern as profile's useProfileData.
  useEffect(() => {
    if (canPurchaseChecked || canPurchaseLoading) return;
    if (!mainUserParams?.Token) return;
    dispatch(
      checkCanPurchasePass({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      }),
    );
  }, [canPurchaseChecked, canPurchaseLoading, mainUserParams?.Token, dispatch]);

  // Direct URL hit by a partner outside the rollout → home.
  if (!isV2Enabled(V2_SCREEN_KEYS.HOW_IT_WORKS, partner)) return <Navigate to="/" replace />;

  // Server said this user may not buy a pass → the purchase link disappears
  // entirely (unlike the discover page's CTA, which stays visible but disabled).
  const hideBuyLink = canPurchaseChecked && canPurchaseNewPass === false;

  return (
    <div className="hiw-v2">
      <ScreenHeader title={t("HOW_IT_WORKS")} onBack={() => navigate(-1)} />

      <div className="hiw-v2-content">
        <p className="hiw-v2-title">{t("HOW_IT_WORKS_TITLE")}</p>
        <p className="hiw-v2-subtitle">{t("HOW_IT_WORKS_SUBTITLE")}</p>

        <InfoSection title={t("HOW_IT_WORKS_PASS_TITLE")} body={t("HOW_IT_WORKS_PASS_BODY")} />
        <InfoSection title={t("HOW_IT_WORKS_SINGLE_TITLE")} body={t("HOW_IT_WORKS_SINGLE_BODY")} />
        <InfoSection title={t("HOW_IT_WORKS_NEXT_TITLE")} body={t("HOW_IT_WORKS_NEXT_BODY")} />

        <div className="hiw-v2-note">
          <p className="hiw-v2-note-title">{t("HOW_IT_WORKS_NOTE_TITLE")}</p>
          <p className="hiw-v2-note-body">{t("HOW_IT_WORKS_NOTE_BODY")}</p>
        </div>

        {!hideBuyLink && (
          <div className="hiw-v2-actions">
            <button
              type="button"
              className="hiw-v2-buy-link"
              onClick={() => navigate("/multipleEntryPassPurchase")}
            >
              {t("HOW_IT_WORKS_BUY_PASS_CTA")}
            </button>
          </div>
        )}
      </div>

      <PartnerFooter />
    </div>
  );
}

export default HowItWorks;
